import { ref } from 'vue';
import mixpanel from '@/mixpanel.ts';

type TooltipOpts = {
  dataPointIndex: number;
  seriesIndex: number;
};

// Convert chart names for download
const formatChartName = (
  origName: string,
  area: string,
  time: string,
  zone: string,
  cluster: string,
  day: string,
) => {
  let chartName = `${origName.replace(/\s/g, '')}`;

  if (area) {
    chartName += `-${area}`;
  }
  if (area === 'Cluster' && cluster) {
    chartName += `-${cluster}`;
  }
  if (area === 'Zone' && zone) {
    chartName += `-${zone}`;
  }
  if (time) {
    chartName += `-${time}`;
  }
  if (day) {
    chartName += `-${day}`;
  }

  return chartName;
};

// Simplify fetching the same name for each exportable file type
const getChartExportSettings = (
  origName: string,
  area: string,
  time: string,
  zone: string,
  cluster: string,
  day: string,
) => {
  const chartName = formatChartName(origName, area, time, zone, cluster, day);
  return {
    show: true,
    export: {
      csv: {
        filename: chartName,
      },
      svg: {
        filename: chartName,
      },
      png: {
        filename: chartName,
      },
    },
  };
};

const getDefaultChartOptions = (chartCode: string) => {
  const chartOptions = defaultChartOptions;
  chartOptions.value.chart.events = {
    click: (event: MouseEvent) => {
      const target = event.target as HTMLDivElement;
      const clickedClass = target?.classList;
      if (clickedClass.contains('exportSVG')) {
        mixpanel.track('Chart Download', {
          widget: chartCode,
          type: 'SVG',
        });
      } else if (clickedClass.contains('exportPNG')) {
        mixpanel.track('Chart Download', {
          widget: chartCode,
          type: 'PNG',
        });
      } else if (clickedClass.contains('exportCSV')) {
        mixpanel.track('Chart Download', {
          widget: chartCode,
          type: 'CSV',
        });
      }
    },
  };
  return chartOptions;
};

export type BaseChartOptions = {
  annotations?: {
    points?: {
      x: string;
      y: number;
      label: {
        text: string;
        borderColor: string;
        style: {
          background: string;
        };
      };
      marker: {
        size: number;
      };
    }[];
  };

  chart: {
    id?: string;
    type?: string; // Should not be optional, needs to be set on each chart.
    toolbar?: {
      show?: boolean;
      export?: {
        csv: {
          filename: string;
        };
        svg: {
          filename: string;
        };
        png: {
          filename: string;
        };
      };
    };
    parentHeightOffset?: number;
    stacked?: boolean;
    width?: string | number;
    height?: string | number;
    zoom?: {
      enabled: boolean;
    };
    events?: {
      click?(event: MouseEvent): void;
    };
  };

  colors?: string[];

  dataLabels?: {
    dropShadow?: {
      enabled?: boolean;
    };
    enabled?: boolean;
    textAnchor?: string;
    formatter?(val: string | number, op?: { value: number }): string | string[];
    offsetX?: number;
    style?: {
      colors?: string[];
      fontSize?: string;
      fontWeight?: string;
    };
  };

  fill?: {
    opacity: number;
  };

  grid?: {
    show: boolean;
  };

  labels?: string[];

  legend?: {
    floating?: boolean;
    fontFamily?: string;
    horizontalAlign?: string;
    offsetY?: number;
    position?: string;
  };

  markers?: {
    colors: string;
    strokeColors: string;
    strokeWidth: number;
    size: number;
  };

  plotOptions?: {
    bar?: {
      borderRadius?: number;
      borderRadiusApplication?: string;
      horizontal: boolean;
    };
    pie?: {
      customScale: number;
      donut: {
        size: string;
      };
    };
    treemap?: {
      distributed: boolean;
      enableShades: boolean;
    };
  };

  stroke?: {
    curve?: string;
    width?: number;
  };

  title?: {
    align?: string;
    text?: string;
    style?: {
      color: string;
      fontFamily: string;
      fontSize: string;
      fontWeight: string;
    };
  };

  tooltip?: {
    x?: {
      formatter?(val: string | number, opts?: TooltipOpts): string;
    };
    y?: {
      formatter?(val: string | number, opts?: TooltipOpts): string;
    };
  };

  xaxis?: {
    categories?: (string | undefined)[];
    show?: boolean;
    type?: string;
    labels?: {
      hideOverlappingLabels?: boolean;
      rotate?: number;
      rotateAlways?: boolean;
      formatter?(val: string): string;
      hideOverlappingLabels?: boolean;
      rotate?: number;
      format?: string;
    };
    tooltip?: {
      enabled?: boolean;
    };
    title?: {
      text?: string;
    };
  };

  yaxis?: {
    labels?: {
      formatter?(val: string | number): string;
    };
    show: boolean;
    title?: {
      text: string;
    };
  };
};

const defaultChartOptions = ref<BaseChartOptions>({
  chart: {
    id: '',
    type: '',
    parentHeightOffset: 0,
    width: '100%',
    zoom: {
      enabled: false,
    },
  },
  fill: {
    opacity: 1,
  },
  legend: {
    position: 'bottom',
    fontFamily: 'Outfit',
  },
  grid: {
    show: false,
  },
});

export { getChartExportSettings, getDefaultChartOptions };
